// SendItEmail.jsx
import React from 'react';
import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import * as XLSX from 'xlsx';

export const sendItEmailMessage = async () => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');
    let combinedHTML = '';

    previewElements.forEach(preview => {
        combinedHTML += preview.innerHTML + '<br><br>';
    });

    const firstPreviewText = previewElements[0]?.innerText || '';
    const snippet = firstPreviewText.substring(0, 100).trim();
    const emailMessage =
        `${snippet}\n\n` +
        `Your email client limits the quantity of content OneWriter can include in it and may remove text styling. ` +
        `Your complete content per your selected preferences has been copied to your clipboard. ` +
        `You can paste the content into the email now.`;

    const subject = encodeURIComponent('OneWriter Export');
    const body = encodeURIComponent(emailMessage);
    const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${subject}&body=${body}`;

    try {
        await navigator.clipboard.write([
            new ClipboardItem({
                'text/html': new Blob([combinedHTML], { type: 'text/html' }),
                'text/plain': new Blob([firstPreviewText], { type: 'text/plain' })
            })
        ]);
    } catch (err) {
        console.warn('Clipboard copy failed:', err);
        alert('Could not copy content to clipboard.');
        return;
    }

    window.open(gmailURL, '_blank');
};

export const sendItEmailXlsx = async () => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to generate XLSX.');
        return;
    }

    // Get filename (same pattern as PDF and DOCX functions)
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export';

    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    console.log(`XLSX filename will be: ${fileName}.xlsx`);

    try {
        // Create a new workbook
        const wb = XLSX.utils.book_new();

        // First, collect all table data with their card titles
        const tableDataByTitle = new Map();
        let globalHeaders = null;

        // Process each preview element (page)
        previewElements.forEach((preview, pageIndex) => {
            // Look for card titles in this preview
            const cardTitles = preview.querySelectorAll('.card-title, .title, h1, h2, h3, h4, h5, h6');
            const tables = preview.querySelectorAll('table');

            if (tables.length > 0) {
                tables.forEach((table, tableIndex) => {
                    // Try to find the associated card title for this table
                    let cardTitle = 'General';

                    // Look for the closest preceding card title
                    if (cardTitles.length > 0) {
                        // Get the last card title that appears before or contains this table
                        for (let i = cardTitles.length - 1; i >= 0; i--) {
                            const titleElement = cardTitles[i];
                            const titleText = (titleElement.innerText || titleElement.textContent || '').trim();

                            // Check if this title comes before the table in DOM order
                            const titlePosition = titleElement.compareDocumentPosition(table);
                            if (titlePosition & Node.DOCUMENT_POSITION_FOLLOWING) {
                                cardTitle = titleText;
                                break;
                            }
                        }
                    }

                    console.log(`Page ${pageIndex + 1}, Table ${tableIndex + 1}: Card title = "${cardTitle}"`);

                    // Extract table headers (set global headers from first table)
                    if (!globalHeaders) {
                        const headerRows = table.querySelectorAll('thead tr, tr:first-child');
                        if (headerRows.length > 0) {
                            const headerRow = headerRows[0];
                            const headerCells = headerRow.querySelectorAll('th, td');
                            globalHeaders = Array.from(headerCells).map(cell =>
                                (cell.innerText || cell.textContent || '').trim()
                            );
                            console.log('Set global headers:', globalHeaders);
                        }
                    }

                    // Extract table body rows
                    const bodyRows = table.querySelectorAll('tbody tr, tr:not(:first-child)');
                    const tableRowData = [];

                    bodyRows.forEach(row => {
                        const cells = row.querySelectorAll('td, th');
                        const rowData = Array.from(cells).map(cell => {
                            let cellContent = (cell.innerText || cell.textContent || '').trim();
                            return cellContent;
                        });

                        // Only add rows that have some content
                        if (rowData.some(cell => cell)) {
                            tableRowData.push(rowData);
                        }
                    });

                    // If no proper table structure found, extract all rows
                    if (!globalHeaders && tableRowData.length === 0) {
                        const allRows = table.querySelectorAll('tr');
                        allRows.forEach((row, rowIndex) => {
                            const cells = row.querySelectorAll('td, th');
                            const rowData = Array.from(cells).map(cell =>
                                (cell.innerText || cell.textContent || '').trim()
                            );
                            if (rowData.some(cell => cell)) {
                                if (rowIndex === 0 && !globalHeaders) {
                                    globalHeaders = rowData;
                                    console.log('Set global headers from first row:', globalHeaders);
                                } else {
                                    tableRowData.push(rowData);
                                }
                            }
                        });
                    }

                    // Store or merge data by card title
                    if (tableDataByTitle.has(cardTitle)) {
                        // Merge with existing data for this card title
                        const existingData = tableDataByTitle.get(cardTitle);
                        existingData.push(...tableRowData);
                        console.log(`Merged ${tableRowData.length} rows with existing data for "${cardTitle}"`);
                    } else {
                        // Store new data for this card title
                        tableDataByTitle.set(cardTitle, [...tableRowData]);
                        console.log(`Added ${tableRowData.length} rows for new card title "${cardTitle}"`);
                    }
                });
            } else {
                // No tables found - add text content as single-column data
                console.log(`No tables found in preview ${pageIndex + 1}, adding as text`);
                const textContent = preview.innerText || preview.textContent || '';
                const lines = textContent.split('\n').filter(line => line.trim());

                // Add a simple header if this is the first content
                if (!globalHeaders) {
                    globalHeaders = ['Content'];
                }

                const textRows = lines.map(line => [line.trim()]).filter(row => row[0]);

                const cardTitle = 'Text Content';
                if (tableDataByTitle.has(cardTitle)) {
                    const existingData = tableDataByTitle.get(cardTitle);
                    existingData.push(...textRows);
                } else {
                    tableDataByTitle.set(cardTitle, textRows);
                }
            }
        });

        // Now combine all the merged data into final worksheet data
        const worksheetData = [];

        // Add headers
        if (globalHeaders && globalHeaders.length > 0) {
            worksheetData.push(globalHeaders);
        }

        // Add all merged table data
        for (const [cardTitle, rowData] of tableDataByTitle) {
            console.log(`Adding ${rowData.length} rows for card title: "${cardTitle}"`);
            worksheetData.push(...rowData);
        }

        // If no data was collected, add placeholder
        if (worksheetData.length === 0) {
            worksheetData.push(['No data found']);
        }

        console.log(`Final combined worksheet data rows: ${worksheetData.length}`);
        console.log('Sample data:', worksheetData.slice(0, 5));

        // Create single worksheet from all the combined data
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // Auto-size columns based on content
        const colWidths = [];
        worksheetData.forEach(row => {
            row.forEach((cell, colIndex) => {
                const cellLength = String(cell).length;
                colWidths[colIndex] = Math.max(colWidths[colIndex] || 10, Math.min(cellLength + 2, 50));
            });
        });

        ws['!cols'] = colWidths.map(width => ({ width }));

        // Add single worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'OneWriter Export');

        // Generate XLSX file
        const xlsxArrayBuffer = XLSX.write(wb, {
            bookType: 'xlsx',
            type: 'array'
        });

        const xlsxBlob = new Blob([xlsxArrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        console.log('Generated XLSX blob size:', xlsxBlob.size);

        // Download the XLSX file
        const url = URL.createObjectURL(xlsxBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Open email client with instructions (same pattern as PDF and DOCX)
        const subject = encodeURIComponent(`OneWriter Export: ${fileName}`);
        const body = encodeURIComponent(
            `The XLSX file "${fileName}.xlsx" has been downloaded to your device. ` +
            `Please attach it to this email before sending.\n\n`
        );
        const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${subject}&body=${body}`;

        // Small delay to ensure download starts first
        setTimeout(() => {
            window.open(gmailURL, '_blank');
        }, 1000);

    } catch (err) {
        console.error('XLSX generation failed:', err);
        alert(`Failed to generate XLSX file: ${err.message}`);
    }
};

export const sendItEmailDocx = async () => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to generate DOCX.');
        return;
    }

    // Get filename (same as PDF function)
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export';

    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    // Extract text content (same approach as sendItEmailMessage but get text instead of HTML)
    let combinedText = '';
    previewElements.forEach((preview, index) => {
        const textContent = preview.innerText || preview.textContent || '';
        combinedText += textContent;
        if (index < previewElements.length - 1) {
            combinedText += '\n\n--- Page Break ---\n\n';
        }
    });

    console.log('Combined text for DOCX:', combinedText.substring(0, 200));

    if (!combinedText.trim()) {
        alert('No text content found in preview elements.');
        return;
    }

    try {
        // Create DOCX document using docx library
        const paragraphs = combinedText.split('\n').map(line => {
            return new Paragraph({
                children: [
                    new TextRun({
                        text: line || ' ', // Empty line if line is empty
                        font: 'Arial',
                        size: 24, // 12pt font (size is in half-points)
                    }),
                ],
            });
        });

        // Create document
        const doc = new Document({
            sections: [{
                properties: {},
                children: [
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: fileName,
                                font: 'Arial',
                                size: 32, // 16pt font for title
                                bold: true,
                            }),
                        ],
                    }),
                    new Paragraph({ children: [new TextRun(' ')] }), // Empty line
                    ...paragraphs,
                ],
            }],
        });

        // Generate DOCX blob
        const docxBlob = await Packer.toBlob(doc);

        console.log('Generated DOCX blob size:', docxBlob.size);

        // Download the DOCX file
        const url = URL.createObjectURL(docxBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Open email client with instructions (same pattern as PDF)
        const subject = encodeURIComponent(`OneWriter Export: ${fileName}`);
        const body = encodeURIComponent(
            `The DOCX file "${fileName}.docx" has been downloaded to your device. ` +
            `Please attach it to this email before sending.\n\n`
        );
        const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${subject}&body=${body}`;

        // Small delay to ensure download starts first
        setTimeout(() => {
            window.open(gmailURL, '_blank');
        }, 1000);

    } catch (err) {
        console.error('DOCX generation failed:', err);
        alert(`Failed to generate DOCX file: ${err.message}`);
    }
};

export const sendItEmailPDF = (orientationMode = 'portrait') => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to generate PDF.');
        return;
    }

    // Scan for the filename from the DOM
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export'; // Default filename

    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    console.log(`PDF filename will be: ${fileName}.pdf`);

    const container = document.createElement('div');
    container.style.background = 'white';
    container.style.width = orientationMode === 'landscape' ? '11in' : '8.5in';

    previewElements.forEach((preview, index) => {
        const clone = preview.cloneNode(true);

        // Remove the document frame
        clone.style.border = 'none';

        // More targeted fix: only adjust content elements that might have width constraints
        const contentElements = clone.querySelectorAll('.print-preview-content');
        contentElements.forEach(contentEl => {
            // Remove any max-width or fixed width that might be constraining the content
            contentEl.style.maxWidth = 'none';
            contentEl.style.width = 'auto';
        });

        clone.style.width = '100%';
        clone.style.minHeight = orientationMode === 'landscape' ? '7.5in' : '10in';
        clone.style.margin = '0';
        clone.style.padding = '0.5in';
        clone.style.boxSizing = 'border-box';
        clone.style.display = 'block';
        clone.style.fontSize = '16px';
        clone.style.lineHeight = '1.5';

        if (index < previewElements.length - 1) {
            clone.style.pageBreakAfter = 'always';
        }

        container.appendChild(clone);
    });

    document.body.appendChild(container);

    const opt = {
        margin: 0,
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            letterRendering: true
        },
        jsPDF: {
            unit: 'in',
            format: 'letter',
            orientation: orientationMode
        }
    };

    html2pdf()
        .set(opt)
        .from(container)
        .outputPdf('blob')
        .then((pdfBlob) => {
            // Create a download for the PDF
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileName}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            container.remove();

            // Open email client with instructions
            const subject = encodeURIComponent(`OneWriter Export: ${fileName}`);
            const body = encodeURIComponent(
                `The PDF file "${fileName}.pdf" has been downloaded to your device. ` +
                `Please attach it to this email before sending.\n\n`
            );
            const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${subject}&body=${body}`;

            // Small delay to ensure download starts first
            setTimeout(() => {
                window.open(gmailURL, '_blank');
            }, 1000);
        })
        .catch((err) => {
            console.error('PDF generation failed:', err);
            container.remove();
        });
};
